import validator from "validator";
import * as htmlToImage from "html-to-image";
import * as FileSaver from "file-saver";
import { parse, stringify } from "flatted";
import { jsPDF } from "jspdf";
import sanitize from "sanitize-filename";
import QRCode from "qrcode";

import i18n from "../../../plugins/i18n";
import { formatPrice, getCountryCurrencyRate } from "./currencyHelper";

export const supportedLanguages = () => ["en", "ar"];

export const wait = async (waitTime = 3000) => {
  await new Promise(resolve => setTimeout(resolve, waitTime));
};

export const hasContents = contents => {
  return contents && contents.length;
};

export const getMediaUrl = (media, { index = 0, params } = {}) => {
  const medias =
    media != null
      ? getMediaUrls(media instanceof Array ? media : [media], { params })
      : null;

  if (medias && medias.length > 0) {
    if (index && medias.length >= index + 1) return medias[index];
    return medias[0];
  }
};

export const getMediaUrls = (medias, { params } = {}) => {
  return (medias || []).reduce((result, item) => {
    let urlPath = item ? item.url || item : null;

    if (typeof urlPath === "string") {
      urlPath = urlPath.trim();

      if (urlPath.length > 0) {
        if (validator.isURL(urlPath)) {
          result.push(`${urlPath}${params ?? ""}`);
        } else if (urlPath.startsWith("files/")) {
          result.push(
            `${process.env.VUE_APP_S3_BASE_URL}/${urlPath}${params ?? ""}`
          );
        }
      }
    }

    return result;
  }, []);
};

export const getImage = getMediaUrl;

export const getImages = getMediaUrls;

export const getVideo = getMediaUrl;

export const getVideos = getMediaUrls;

export const getAudio = getMediaUrl;

export const getAudios = getMediaUrls;

export const capitalizeFirstLetter = string => {
  return string ? string.charAt(0).toUpperCase() + string.slice(1) : string;
};

export const getLocalisedValue = (
  localisedDataList,
  { uppercase = false, capitalize = true, preferredLocale, emptyNotation } = {}
) => {
  localisedDataList = localisedDataList ?? [];
  if (!localisedDataList.length) return emptyNotation ?? "";

  let localisedValue =
    localisedDataList
      .find(x => x.lang === (preferredLocale ?? i18n.locale.toString()))
      ?.value?.trim() ?? "";

  if (!localisedValue?.length) {
    localisedValue = localisedDataList[0].value?.trim() ?? emptyNotation ?? "";
  }

  return uppercase === true
    ? localisedValue.toUpperCase()
    : capitalize
    ? capitalizeFirstLetter(localisedValue)
    : localisedValue;
};

export const getCountryPrice = (
  price,
  { format = true, convert = true, currency, currencyRate }
) => {
  try {
    if (price != null) {
      if (!currency && !currencyRate) {
        const countryCurrencyRate = getCountryCurrencyRate();
        currency = countryCurrencyRate.currency;
        currencyRate = countryCurrencyRate.currencyRate;
      }

      if (convert && currencyRate) price *= currencyRate;
      return format === true ? formatPrice(price, currency) : price;
    }
  } catch (e) {
    console.log("coreHelper.getCountryPrice:exception:", e.message);
  }
};

export const downloadFile = ({ data, fileName }) => {
  if (window.saveAs) {
    window.saveAs(data, fileName || `image-${Date.now()}.png`);
  } else {
    FileSaver.saveAs(data, fileName || `image-${Date.now()}.png`);
  }
};

export const printHtmlAsPdf = async ({
  htmlTemplate,
  windowHeight = 600,
  windowWidth = 800,
}) => {
  if (htmlTemplate) {
    const browserWindow = window.open(
      "",
      "",
      `left=0,top=0,height=${windowHeight},width=${windowWidth},toolbar=0,scrollbars=0,status=0`
    );

    browserWindow.document.write(htmlTemplate);
    browserWindow.document.close();
    browserWindow.focus();
    browserWindow.print();
  }
};

export const downloadHtmlAsPdf = async ({
  htmlTemplate,
  data,
  windowWidth = 200,
  windowHeight = 200,
  imageWidth,
  imageHeight,
  imageScaling,
  orientation = "portrait",
}) => {
  let browserWindow, result;

  if (data) {
    if (htmlTemplate) {
      browserWindow = window.open(
        "",
        "",
        `left=0,top=0,width=${windowWidth},height=${windowHeight},toolbar=0,scrollbars=0,status=0`
      );

      browserWindow.document.write(htmlTemplate);
      browserWindow.document.close();
    }

    if (!data.htmlElement && data.elementId && browserWindow) {
      data.htmlElement = browserWindow.document.getElementById(data.elementId);
    }

    if (data.htmlElement) {
      const canvas = await downloadHtmlAsImage({
        imageWidth,
        imageHeight,
        imageScaling: 2,
        dataList: [data],
        download: false,
        toCanvas: true,
      });

      if (canvas?.length) {
        const imgData = canvas[0].toDataURL("image/png");

        const doc = new jsPDF({
          orientation,
          unit: "px",
          format: [imageWidth, imageHeight],
          putOnlyUsedFonts: true,
          hotfixes: ["px_scaling"],
        });

        doc.addImage(imgData, "PNG", 0, 0, imageWidth, imageHeight);
        doc.save(data.fileName);
      }

      // result = await htmlToPdf(data.htmlElement, {
      //   filename: data.fileName,
      //   html2canvas: {
      //     scale: imageScaling,
      //     useCORS: true,
      //     // width: imageWidth,
      //     // height: imageHeight,
      //   },
      //   jsPDF: {
      //     orientation: "landscape",
      //     unit: "px",
      //     format: [imageWidth, imageHeight],
      //     putOnlyUsedFonts: true,
      //     hotfixes: ["px_scaling"],
      //   },
      // }).catch(error => {
      //   console.log("downloadHtmlAsPdf:exception", error);
      // });
    }

    if (browserWindow) browserWindow.close();
    return result;
  }
};

export const downloadHtmlAsImage = async ({
  htmlTemplate,
  dataList,
  windowWidth = 200,
  windowHeight = 200,
  download = true,
  imageWidth,
  imageHeight,
  imageScaling,
  toCanvas = false,
}) => {
  let browserWindow,
    results = [],
    htmlToImageOptions = {
      canvasWidth: imageWidth,
      canvasHeight: imageHeight,
      cacheBust: true,
      imagePlaceholder: require("@/app/core-module/assets/images/LOGO.png"),
      pixelRatio: imageScaling,
      skipFonts: true,
    };

  if (dataList && dataList.length) {
    if (htmlTemplate) {
      browserWindow = window.open(
        "",
        "",
        `left=0,top=0,width=${windowWidth},height=${windowHeight},toolbar=0,scrollbars=0,status=0`
      );

      browserWindow.document.write(htmlTemplate);
      browserWindow.document.close();
    }

    for (const data of dataList) {
      if (!data.htmlElement && data.elementId && browserWindow) {
        data.htmlElement = browserWindow.document.getElementById(
          data.elementId
        );
      }

      if (data.htmlElement) {
        const htmlToImagePromise = download
          ? htmlToImage.toBlob(data.htmlElement, htmlToImageOptions)
          : toCanvas
          ? htmlToImage.toCanvas(data.htmlElement, htmlToImageOptions)
          : htmlToImage.toSvg(data.htmlElement, htmlToImageOptions);

        await htmlToImagePromise
          .then(function (imageData) {
            if (download) {
              downloadFile({ data: imageData, fileName: data.fileName });
            }

            results.push(imageData);
          })
          .catch(error => {
            console.log("downloadHtmlAsImage:exception", error);
          });
      }
    }

    if (browserWindow) browserWindow.close();
    return results;
  }
};

export const filterEmptyLocalisedValues = values => {
  return values?.filter(x => x.value?.trim()?.length);
};

export const filterEmptyMediaUrls = values => {
  return values?.filter(x => x.url != null);
};

export const getPositiveInteger = value => {
  try {
    value = getInteger(value);
    if (value && value >= 0) return value;
  } catch (_) {}
};

export const getInteger = (value, allowNegative = false) => {
  const toInteger = () => {
    try {
      value = value?.toString()?.trim() ?? "";

      if (value.length) {
        value = parseInt(Number(value).toFixed());
        if (Number.isInteger(value)) return value;
      }
    } catch (_) {}
  };

  value = toInteger();

  if (value != null) {
    return allowNegative ? value : value < 0 ? value * -1 : value;
  }
};

export const getFloat = (value, allowNegative = false) => {
  const toFloat = () => {
    try {
      value = value?.toString()?.trim() ?? "";

      if (value.length) {
        value = parseFloat(value);
        if (Number(value)) return value;
      }
    } catch (_) {}
  };

  value = toFloat();

  if (value != null) {
    return allowNegative ? value : value < 0 ? value * -1 : value;
  }
};

export const numberInputEventFilter = ({
  event,
  isFloat = false,
  allowNegative = false,
}) => {
  if (event.type === "paste") {
    const pastedValue = event.clipboardData?.getData("Text");
    if (isNaN(pastedValue) || (pastedValue < 0 && !allowNegative)) {
      event.preventDefault();
    } else {
      return;
    }
  }

  let digits = [...Array(10).keys()].map(i => i + 48); //[0-9] => [48-57]
  const comma = [44, 108, 188]; //[,]
  const minus = [45, 109, 173, 189]; //[-]
  const decimal = [46, 110, 190]; //[.]

  if (isFloat) digits = [...digits, ...decimal];
  if (allowNegative) digits = [...digits, ...minus];

  if (!digits.includes(event.charCode)) {
    event.preventDefault();
  }
};

export const deepCloneObject = obj => {
  try {
    return parse(stringify(obj));
  } catch (e) {
    console.log("coreHelper.deepCloneObject:", e.message);
    return obj;
  }
};

export const textAbstract = ({ text, maxLength = 20, delimiter = "..." }) => {
  if (text == null) return "";
  if (text.length <= maxLength) return text;

  text = text.substring(0, maxLength);
  const last = text.lastIndexOf(" ");
  text = text.substring(0, last);
  return text + delimiter;
};

export const noPaginationInput = () => ({ skip: 0, limit: 0 });

export const defaultPageInput = limit => ({ skip: 0, limit: limit ?? 100 });

export const defaultPagination = limit => ({
  page: 1,
  itemsPerPage: limit ?? 100,
});

export const cleanStringValue = value => {
  return value?.trim()?.replaceAll(/\t/g, " ")?.replaceAll(/\s\s+/g, " ") ?? "";
};

export const sanitizeStringValue = value => {
  // const invalidChars = /[<>:"\/\\|?*\x00-\x1F]/g;
  // return value?.replace(invalidChars, "_");
  return sanitize(value ?? "", { replacement: "_" });
};

export const getQrCode = async (value, size) => {
    try {
        return await QRCode.toDataURL(value)
        // return `https://image-charts.com/chart?cht=qr&choe=UTF-8&chof=.png&icqrf=191A18&icqrb=ffff0000&chs=${size}x${size}&chl=${value}`;
        // return `https://quickchart.io/qr?size=${size}&text=${value}`;
    } catch(e) {
        console.error("Failed to get QR code:", e.message);
    }
}